import { useMemo, useState, type ReactNode } from 'react';
import {
  Badge,
  Field,
  HStack,
  InputGroup,
  Span,
  Text,
} from '@chakra-ui/react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { RiftboundIconButton } from '../ui/icon-button';
import { RiftboundInput } from '../ui/input';

interface PasswordFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  autoComplete?: string;
  /** Show strength hint badges when the field is focused (use for new-password fields). */
  showStrengthHints?: boolean;
  /** When provided, shows a ✓/✗ match indicator after the field is blurred. */
  matchAgainst?: string;
  /**
   * Show a character count under the field (not inside the input — trailing
   * adornments fight placeholder/password text on narrow screens).
   */
  showCount?: boolean;
  size?: 'md' | 'lg';
  /** Optional control rendered on the label row (e.g. Forgot password). */
  labelTrailing?: ReactNode;
}

export function PasswordField({
  label,
  value,
  onChange,
  placeholder = 'Enter password',
  autoComplete = 'current-password',
  showStrengthHints = false,
  matchAgainst,
  showCount = false,
  size = 'md',
  labelTrailing,
}: PasswordFieldProps) {
  const [show, setShow] = useState(false);
  const [focused, setFocused] = useState(false);
  const [blurred, setBlurred] = useState(false);

  const reqs = useMemo(() => ({
    minLength: value.length >= 6,
    uppercase: /[A-Z]/.test(value),
    special: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(value),
  }), [value]);

  const passwordsMatch = matchAgainst !== undefined
    ? value === matchAgainst && value.length > 0
    : null;

  return (
    <Field.Root w="full" gap={1.5}>
      <HStack justify="space-between" align="baseline" w="full" gap={3} minH="1.25rem">
        <Field.Label fontSize="sm" fontWeight="medium" mb={0}>
          {label}
        </Field.Label>
        {labelTrailing}
      </HStack>

      <InputGroup
        w="full"
        endElement={
          <RiftboundIconButton
            aria-label={show ? 'Hide password' : 'Show password'}
            onClick={() => setShow((p) => !p)}
            variant="ghost"
            size="sm"
            color="textMuted"
            me={0.5}
          >
            {show ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
          </RiftboundIconButton>
        }
      >
        <RiftboundInput
          w="full"
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            setBlurred(true);
          }}
          placeholder={placeholder}
          size={size}
          maxLength={50}
          autoComplete={autoComplete}
        />
      </InputGroup>

      {showCount ? (
        <Text fontSize="xs" color="textMuted" textAlign="right">
          {value.length}/50
        </Text>
      ) : null}

      {showStrengthHints && focused && (
        <HStack gap={1} flexWrap="wrap">
          <Badge colorPalette={reqs.minLength ? 'green' : 'yellow'} size="sm">Min 6 chars</Badge>
          <Badge colorPalette={reqs.uppercase ? 'green' : 'yellow'} size="sm">Uppercase</Badge>
          <Badge colorPalette={reqs.special ? 'green' : 'yellow'} size="sm">Special char</Badge>
        </HStack>
      )}

      {matchAgainst !== undefined && blurred && value.length > 0 && (
        <Field.HelperText mt={0}>
          <Span color={passwordsMatch ? 'green.400' : 'red.400'}>
            {passwordsMatch ? '✓ Passwords match' : '✗ Passwords do not match'}
          </Span>
        </Field.HelperText>
      )}
    </Field.Root>
  );
}
